import React from 'react';
import { Box, Button, Card, CardContent, Grid, Paper, Skeleton, TableCell, TableRow, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { cardSx } from '../theme';

/**
 * Placeholders shaped like the content they stand in for, so a page renders its real
 * frame (title, buttons, table headers) immediately and only the data slots fill in.
 * A centered spinner showed a blank page and then shifted everything into place.
 */

// Read by screen readers, invisible on screen (@mui/utils' visuallyHidden, which isn't a
// direct dependency here).
const visuallyHidden = {
  border: 0, clip: 'rect(0 0 0 0)', height: '1px', margin: '-1px', overflow: 'hidden',
  padding: 0, position: 'absolute', whiteSpace: 'nowrap', width: '1px',
};

// Skeleton's pulse is a slow looping opacity change — exactly the kind of oscillation
// reduced-motion users opt out of. Keep the shapes, drop the pulse.
export const useSkeletonAnimation = () =>
  useMediaQuery('(prefers-reduced-motion: reduce)') ? false : 'pulse';

// Varied widths read as text; identical bars read as a broken table.
const WIDTHS = ['70%', '55%', '85%', '45%', '65%', '75%', '50%', '60%'];

/** Rows for a <TableBody> while its first load is in flight. */
export const TableSkeletonRows = ({ columns, rows = 8 }) => {
  const animation = useSkeletonAnimation();
  return Array.from({ length: rows }, (_, r) => (
    <TableRow key={`skeleton-${r}`} aria-hidden="true" sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
      {Array.from({ length: columns }, (__, c) => (
        <TableCell key={c}>
          <Skeleton animation={animation} width={WIDTHS[(r + c) % WIDTHS.length]} />
        </TableCell>
      ))}
    </TableRow>
  ));
};

/** A few text lines inside the same Card a horse-detail tab section renders in. */
export const CardSkeleton = ({ lines = 3, title = true, label = 'Loading…' }) => {
  const animation = useSkeletonAnimation();
  return (
    <Card aria-busy="true">
      <CardContent>
        <Box component="span" sx={visuallyHidden}>{label}</Box>
        {title && <Skeleton animation={animation} width="30%" height={32} sx={{ mb: 1 }} />}
        {Array.from({ length: lines }, (_, i) => (
          <Skeleton key={i} animation={animation} width={WIDTHS[i % WIDTHS.length]} />
        ))}
      </CardContent>
    </Card>
  );
};

/**
 * Detail pages (a horse, a breeding record) can't render their real frame without the
 * record — the title *is* the record. The back button doesn't depend on it, so that one
 * is real (and works mid-load); the header card takes the page's own styles so the
 * placeholder has the same colour and size as what replaces it.
 */
export const DetailPageSkeleton = ({ backLabel, onBack, heroSx, dark = false, cards = 4, label = 'Loading…' }) => {
  const animation = useSkeletonAnimation();
  // Default skeleton grey disappears on the dark gradient header; use a light tint there.
  const heroBone = dark ? { bgcolor: 'rgba(255,255,255,0.18)' } : undefined;
  return (
    <Box aria-busy="true">
      <Box component="span" sx={visuallyHidden}>{label}</Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        {backLabel}
      </Button>
      <Paper sx={{ ...cardSx, p: 3, mb: 3, ...heroSx }}>
        <Skeleton animation={animation} width="40%" height={48} sx={heroBone} />
        <Skeleton animation={animation} width="25%" sx={heroBone} />
      </Paper>
      <Grid container spacing={2}>
        {Array.from({ length: cards }, (_, i) => (
          <Grid item xs={12} md={6} key={i}>
            <Paper sx={{ ...cardSx, p: 2 }}>
              <Skeleton animation={animation} width="35%" height={28} sx={{ mb: 1 }} />
              {[0, 1, 2, 3].map((l) => (
                <Skeleton key={l} animation={animation} width={WIDTHS[(i + l) % WIDTHS.length]} />
              ))}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

/**
 * An editable form while its record loads. Rendering the real, empty form instead would
 * let someone start typing only to have the loaded values overwrite it.
 */
export const FormSkeleton = ({ fields = 6, label = 'Loading…' }) => {
  const animation = useSkeletonAnimation();
  return (
    <Paper sx={{ ...cardSx, p: 3 }} aria-busy="true">
      <Box component="span" sx={visuallyHidden}>{label}</Box>
      <Skeleton animation={animation} width="25%" height={32} sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {Array.from({ length: fields }, (_, i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Skeleton animation={animation} variant="rounded" height={40} />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};
